import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Search, UserPlus, ShoppingBag, X, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import CollectPaymentDialog from "@/components/admin/CollectPaymentDialog";

interface ClientHit {
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
}

interface ServiceCategory { id: string; title: string; }
interface SubService { id: string; category_id: string; name: string; price: number; duration: string; }
interface ServiceAddon { id: string; category_id: string; name: string; price: number; duration: string; sort_order: number; }

interface BookingForDialog {
  id: string;
  user_id: string;
  service_name: string;
  service_category?: string;
  booking_date: string;
  booking_time: string;
  duration?: string;
  price: number;
  discount_applied?: number | null;
  deposit_paid_amount?: number | null;
}

const formatTime = (d: Date) => {
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const am = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${m} ${am}`;
};

export const WalkInCheckoutCard = () => {
  const { toast } = useToast();

  // Mode: existing client vs walk-in
  const [mode, setMode] = useState<"existing" | "walkin">("existing");

  // Existing client search
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ClientHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientHit | null>(null);
  const searchTimer = useRef<number | null>(null);

  // Walk-in fields
  const [walkInName, setWalkInName] = useState("");
  const [walkInPhone, setWalkInPhone] = useState("");
  const [walkInEmail, setWalkInEmail] = useState("");

  // Service selection
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [subServices, setSubServices] = useState<SubService[]>([]);
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState<string>("");
  const [subServiceId, setSubServiceId] = useState<string>("");
  const [serviceName, setServiceName] = useState("");
  const [servicePrice, setServicePrice] = useState<string>("");
  const [serviceDuration, setServiceDuration] = useState<string>("0 min");

  const [creating, setCreating] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState<BookingForDialog | null>(null);
  const [paymentClientName, setPaymentClientName] = useState<string>("");

  // Load services / categories / addons once
  useEffect(() => {
    (async () => {
      const [{ data: cats }, { data: subs }, { data: ads }] = await Promise.all([
        supabase.from("service_categories").select("id,title").order("sort_order"),
        supabase.from("sub_services").select("id,category_id,name,price,duration").order("sort_order"),
        supabase.from("service_addons").select("id,category_id,name,price,duration,sort_order").order("sort_order"),
      ]);
      setCategories(cats || []);
      setSubServices((subs as any) || []);
      setAddons((ads as any) || []);
    })();
  }, []);

  // Debounced client search
  useEffect(() => {
    if (mode !== "existing") return;
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    const term = search.trim();
    if (term.length < 2) { setResults([]); return; }
    searchTimer.current = window.setTimeout(async () => {
      setSearching(true);
      const digits = term.replace(/\D/g, "");
      let query = supabase
        .from("profiles")
        .select("user_id,full_name,email,phone")
        .limit(8);
      if (digits.length >= 3 && digits.length === term.replace(/[^0-9+\s()-]/g, "").length) {
        query = query.ilike("phone", `%${digits}%`);
      } else {
        query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
      }
      const { data } = await query;
      setResults((data as any) || []);
      setSearching(false);
    }, 250);
    return () => { if (searchTimer.current) window.clearTimeout(searchTimer.current); };
  }, [search, mode]);

  const filteredSubs = useMemo(
    () => subServices.filter((s) => s.category_id === categoryId),
    [subServices, categoryId]
  );

  const filteredAddons = useMemo(
    () => addons.filter((a) => a.category_id === categoryId),
    [addons, categoryId]
  );

  const selectedAddons = useMemo(
    () => addons.filter((a) => selectedAddonIds.includes(a.id)),
    [addons, selectedAddonIds]
  );

  const addonsTotal = useMemo(
    () => selectedAddons.reduce((sum, a) => sum + Number(a.price || 0), 0),
    [selectedAddons]
  );

  const toggleAddon = (id: string) => {
    setSelectedAddonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handlePickSub = (id: string) => {
    setSubServiceId(id);
    const s = subServices.find((x) => x.id === id);
    if (s) {
      setServiceName(s.name);
      setServicePrice(String(Number(s.price || 0).toFixed(2)));
      setServiceDuration(s.duration || "0 min");
    }
  };

  const resetAll = () => {
    setSearch(""); setResults([]); setSelectedClient(null);
    setWalkInName(""); setWalkInPhone(""); setWalkInEmail("");
    setCategoryId(""); setSubServiceId("");
    setServiceName(""); setServicePrice(""); setServiceDuration("0 min");
    setSelectedAddonIds([]);
  };

  const ensureUserId = async (): Promise<{ user_id: string; display_name: string } | null> => {
    if (mode === "existing") {
      if (!selectedClient) {
        toast({ title: "Select a client", description: "Search and pick a client to check out.", variant: "destructive" });
        return null;
      }
      return {
        user_id: selectedClient.user_id,
        display_name: selectedClient.full_name || selectedClient.email || selectedClient.phone || "Client",
      };
    }
    // walk-in
    if (!walkInName.trim()) {
      toast({ title: "Walk-in name required", description: "Enter at least a name for the walk-in client.", variant: "destructive" });
      return null;
    }
    const { data, error } = await supabase.functions.invoke("admin-auth", {
      body: {
        action: "create_walkin_client",
        full_name: walkInName.trim(),
        phone: walkInPhone.trim() || undefined,
        email: walkInEmail.trim() || undefined,
      },
    });
    if (error || !data?.user_id) {
      toast({
        title: "Could not create walk-in",
        description: error?.message || data?.error || "Try again or use an existing client.",
        variant: "destructive",
      });
      return null;
    }
    return { user_id: data.user_id, display_name: walkInName.trim() };
  };

  const handleStartCheckout = async () => {
    if (!serviceName.trim()) {
      toast({ title: "Pick a service", description: "Choose a service or enter a custom one.", variant: "destructive" });
      return;
    }
    const priceNum = Number(servicePrice);
    if (!(priceNum >= 0)) {
      toast({ title: "Invalid price", description: "Enter a valid price.", variant: "destructive" });
      return;
    }

    setCreating(true);
    try {
      const ensured = await ensureUserId();
      if (!ensured) { setCreating(false); return; }

      const now = new Date();
      const bookingDateStr = now.toISOString().slice(0, 10);
      const bookingTimeStr = formatTime(now);
      const categoryTitle = categories.find((c) => c.id === categoryId)?.title || "Walk-In";

      const { data: inserted, error } = await supabase
        .from("bookings")
        .insert({
          user_id: ensured.user_id,
          service_name: serviceName.trim(),
          service_category: categoryTitle,
          booking_date: bookingDateStr,
          booking_time: bookingTimeStr,
          duration: serviceDuration || "0 min",
          price: priceNum,
          status: "confirmed",
          payment_status: "pending",
          payment_type: null,
          notes: mode === "walkin" ? "Walk-in checkout" : "Admin checkout",
          created_by_admin: true,
          // Walk-ins are being served right now — intentionally exempt from the
          // overlap check so checkout is never blocked at the desk.
          blocked_day_override: true,
        })
        .select("id,user_id,service_name,service_category,booking_date,booking_time,duration,price,discount_applied,deposit_paid_amount")
        .single();

      if (error || !inserted) {
        toast({ title: "Could not create booking", description: error?.message || "Unknown error", variant: "destructive" });
        setCreating(false);
        return;
      }

      // Insert any selected add-ons as already-confirmed rows. The
      // `apply_confirmed_addon_to_booking` trigger automatically rolls their
      // price + duration into the booking total.
      let bookingForDialog: any = inserted;
      if (selectedAddons.length > 0) {
        const { data: { user: adminUser } } = await supabase.auth.getUser();
        const addonRows = selectedAddons.map((a) => ({
          booking_id: inserted.id,
          user_id: ensured.user_id,
          addon_id: a.id,
          name: a.name,
          price: a.price,
          duration: a.duration,
          status: "confirmed",
          requested_by: "admin",
          requested_by_user: adminUser?.id ?? null,
          confirmed_by: adminUser?.id ?? null,
        }));
        const { error: addonErr } = await supabase.from("booking_addons").insert(addonRows);
        if (addonErr) {
          toast({
            title: "Add-ons failed",
            description: addonErr.message + " — proceeding without them.",
            variant: "destructive",
          });
        } else {
          // Re-fetch so the payment dialog sees the updated price/duration.
          const { data: refreshed } = await supabase
            .from("bookings")
            .select("id,user_id,service_name,service_category,booking_date,booking_time,duration,price,discount_applied,deposit_paid_amount").is("deleted_at", null)
            .eq("id", inserted.id)
            .single();
          if (refreshed) bookingForDialog = refreshed;
        }
      }

      setPaymentClientName(ensured.display_name);
      setPaymentBooking(bookingForDialog as BookingForDialog);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Card id="checkout" className="bg-card border-border/50 max-w-xl scroll-mt-24">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <ShoppingBag size={16} />
            Checkout
          </CardTitle>
          <p className="text-xs text-muted-foreground/80 mt-1">
            Take payment from a walk-in or an existing client. Creates a booking for right now and opens the payment portal.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <Tabs value={mode} onValueChange={(v) => setMode(v as any)}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="existing" className="text-xs gap-1.5">
                <Search size={12} /> Existing client
              </TabsTrigger>
              <TabsTrigger value="walkin" className="text-xs gap-1.5">
                <UserPlus size={12} /> Walk-in
              </TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="space-y-3 mt-4">
              {selectedClient ? (
                <div className="flex items-center justify-between border border-primary/40 bg-primary/5 rounded-md p-3">
                  <div className="text-sm">
                    <div className="font-medium">{selectedClient.full_name || "Unnamed"}</div>
                    <div className="text-xs text-muted-foreground">
                      {selectedClient.phone || selectedClient.email || "no contact info"}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)} className="gap-1 text-xs">
                    <X size={12} /> Change
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                      Search by name, phone or email
                    </Label>
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="e.g. Jane, 555-1234, jane@..."
                        className="pl-9 bg-background border-border/50"
                      />
                    </div>
                  </div>
                  {searching && (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" /> Searching…
                    </div>
                  )}
                  {!searching && results.length > 0 && (
                    <div className="border border-border/50 rounded-md divide-y divide-border/40 max-h-60 overflow-y-auto">
                      {results.map((r) => (
                        <button
                          key={r.user_id}
                          type="button"
                          onClick={() => { setSelectedClient(r); setResults([]); setSearch(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-primary/5 transition-colors"
                        >
                          <div className="text-sm">{r.full_name || "Unnamed"}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {[r.phone, r.email].filter(Boolean).join(" · ") || "no contact info"}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {!searching && search.trim().length >= 2 && results.length === 0 && (
                    <p className="text-xs text-muted-foreground">No matching clients. Switch to "Walk-in" to add a new one.</p>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="walkin" className="space-y-3 mt-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={walkInName}
                  onChange={(e) => setWalkInName(e.target.value)}
                  placeholder="Walk-in client name"
                  className="bg-background border-border/50"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">Phone</Label>
                  <Input
                    value={walkInPhone}
                    onChange={(e) => setWalkInPhone(e.target.value)}
                    placeholder="Optional"
                    className="bg-background border-border/50"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">Email</Label>
                  <Input
                    type="email"
                    value={walkInEmail}
                    onChange={(e) => setWalkInEmail(e.target.value)}
                    placeholder="Optional"
                    className="bg-background border-border/50"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground/80">
                A client account is created so the booking and receipt can be linked. Phone/email are optional but recommended for receipts.
              </p>
            </TabsContent>
          </Tabs>

          {/* Service selection */}
          <div className="space-y-3 pt-2 border-t border-border/40">
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Service</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Category</Label>
                <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setSubServiceId(""); setSelectedAddonIds([]); }}>
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue placeholder="Pick category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Service</Label>
                <Select
                  value={subServiceId}
                  onValueChange={handlePickSub}
                  disabled={!categoryId || filteredSubs.length === 0}
                >
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue placeholder={categoryId ? "Pick service" : "Pick category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSubs.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} — ${Number(s.price).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground mb-1 block">Service name</Label>
                <Input
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="e.g. Manicure"
                  className="bg-background border-border/50"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={servicePrice}
                  onChange={(e) => setServicePrice(e.target.value)}
                  placeholder="0.00"
                  className="bg-background border-border/50"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Duration</Label>
              <Input
                value={serviceDuration}
                onChange={(e) => setServiceDuration(e.target.value)}
                placeholder="e.g. 1 hr 30 min"
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Add-ons */}
          {categoryId && (
            <div className="space-y-2 pt-2 border-t border-border/40">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1.5">
                  <Plus size={12} /> Add-ons
                </p>
                {selectedAddons.length > 0 && (
                  <span className="text-[11px] text-primary">
                    {selectedAddons.length} selected · +${addonsTotal.toFixed(2)}
                  </span>
                )}
              </div>
              {filteredAddons.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/80">
                  No add-ons available for this category.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filteredAddons.map((a) => {
                    const checked = selectedAddonIds.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAddon(a.id)}
                        className={cn(
                          "flex items-start gap-2 text-left p-2.5 rounded-md border transition-all",
                          checked
                            ? "border-primary bg-primary/10"
                            : "border-border/50 hover:border-primary/40"
                        )}
                      >
                        <Checkbox checked={checked} className="mt-0.5 pointer-events-none" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{a.name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            +${Number(a.price).toFixed(2)}
                            {a.duration && a.duration !== "0 min" ? ` · ${a.duration}` : ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedAddons.length > 0 && (
                <div className="text-[11px] text-muted-foreground/80 italic pt-1">
                  Add-on totals are added automatically when the booking is created.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetAll}
              className="text-xs tracking-widest uppercase text-muted-foreground"
            >
              Clear
            </Button>
            <Button
              onClick={handleStartCheckout}
              disabled={creating}
              className="text-xs tracking-widest uppercase gap-1"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
              Start Checkout
            </Button>
          </div>
        </CardContent>
      </Card>

      <CollectPaymentDialog
        open={!!paymentBooking}
        onOpenChange={(o) => { if (!o) { setPaymentBooking(null); resetAll(); } }}
        booking={paymentBooking}
        clientName={paymentClientName}
        onSaved={() => {
          toast({ title: "Checkout complete", description: "Payment recorded and booking marked completed." });
        }}
      />
    </>
  );
};

export default WalkInCheckoutCard;
